import type { AppDispatch } from '../store/configureStore';
import i18n from '../services/i18n';
import { getAuth0Client } from '../services/auth0';
import { loginError, logoutSuccess } from '../reducers/auth';
import {
  SESSION_MAX_SECONDS, SUPERUSER_KEY, SUPERUSER_ACTIVE_KEY, USER_TARGETS_KEY,
} from '../constants/config';

// Auth thunks for the Universal Login redirect flow. Plain thunks, not
// createAsyncThunk — see the note on `logout`.

const SUPER_USERS = [
  'admpwe@taplause.com',
  'admmla@taplause.com',
  'admpuo@taplause.com',
  'admmno@taplause.com',
];

/** Maps an email to an app role. Used here and by the bootstrap's callback. */
export const resolveRole = (email?: string): 'superuser' | 'admin' =>
  (email && SUPER_USERS.indexOf(email) !== -1) ? 'superuser' : 'admin';

/** Every key the app writes for a session. Shared so the two logouts cannot drift. */
const clearSession = () => {
  localStorage.removeItem('auth0');
  localStorage.removeItem('profile');
  localStorage.removeItem('token');
  localStorage.removeItem(SUPERUSER_KEY);
  localStorage.removeItem(SUPERUSER_ACTIVE_KEY);
  localStorage.removeItem(USER_TARGETS_KEY);
};

/**
 * Writes the session the rest of the app reads. Called by the bootstrap.
 *
 * `expiresAt` is clamped to SESSION_MAX_SECONDS: it caps how long one token is
 * held in localStorage, not the session, which ends at the 8h idle timeout or
 * when the refresh token expires.
 */
export const writeSession = (
  accessToken: string,
  expiresIn: number,
  profile: Record<string, unknown>,
) => {
  const lifetime = Math.min(expiresIn, SESSION_MAX_SECONDS);
  localStorage.setItem('auth0', JSON.stringify({
    accessToken,
    expiresAt: Date.now() + lifetime * 1000,
    profile,
  }));

  const role = resolveRole(profile.email as string | undefined);
  localStorage.setItem(SUPERUSER_KEY, String(role === 'superuser'));
  localStorage.setItem(SUPERUSER_ACTIVE_KEY, 'false');
  return role;
};

/** Clears storage without Redux — the bootstrap has no store yet. */
export const clearSessionStorage = () => clearSession();

/**
 * Redirects to Universal Login. Takes no argument — it has no per-request title,
 * only `ui_locales`. On success it never returns; the bootstrap's callback
 * establishes the session.
 */
export const login = () => {
  return async (dispatch: AppDispatch) => {
    try {
      const client = await getAuth0Client();
      await client.loginWithRedirect({
        authorizationParams: { ui_locales: i18n.language },
      });
    } catch (error) {
      console.error('*** Login failed:%o', error);
      return dispatch(loginError());
    }
  };
};

/**
 * Local logout. Deliberately does NOT end the Auth0 session: it is dispatched
 * from 23 error paths and the idle timer, so a federated version would send the
 * user off to Auth0 on any transient 500. Use `logoutFederated` for that.
 *
 * Must stay a plain thunk returning the dispatched action — createAsyncThunk
 * would emit `auth/logout/pending` instead of the `auth/logoutSuccess` the slice
 * reduces, breaking the wiring and test/actions.js.
 */
export const logout = () => {
  return (dispatch: AppDispatch) => {
    clearSession();
    return dispatch(logoutSuccess());
  };
};

/**
 * Ends the Auth0 session too, so the next login asks for credentials instead of
 * silently signing back in. Header link only.
 *
 * Does not sign the user out of taplause-dashboard — that keeps its own
 * server-side session, which this cannot reach.
 */
export const logoutFederated = () => {
  return async (dispatch: AppDispatch) => {
    clearSession();
    try {
      const client = await getAuth0Client();
      // No logoutSuccess here: the redirect leaves the page anyway, and
      // re-rendering the login box first made it flash before navigating.
      await client.logout({ logoutParams: { returnTo: window.location.origin } });
    } catch (error) {
      // Still on the page, so the UI has to be told the session is gone.
      console.error('*** Auth0 logout failed:%o', error);
      dispatch(logoutSuccess());
    }
  };
};
